import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { TopicRow } from '@contracts/topics';
import { useTopics } from '../api/queries';
import { Broken, Card, Empty, Pill, SearchField, SkeletonRows, Who } from '../ui';
import { ago, dateLabel } from '../lib/time';
import { guessName } from '../lib/note';
import { useUrlText } from '../lib/url-tab';

/**
 * The subjects you keep coming back to (PRD 52).
 *
 * ⚠ **Yours, not the workspace's.** Two people in the same meeting are
 * following different threads through it, and one person's topic draws on
 * meetings the other may not be able to open at all.
 *
 * ## Why three of them are cards and the rest are rows
 *
 * A list of thirty equal rows is a list nobody reads past the first screen,
 * and the whole point of this page is that some subjects are alive right now
 * and most are not. The three most active get the room to show their shape —
 * the trend, the last thing that happened — and everything else is one line
 * with the same trend drawn small. Nothing is hidden; the weight is just
 * where the attention should go.
 */
export function Topics(): ReactElement {
  const topics = useTopics();
  const [needle, setNeedle] = useUrlText('q');

  const shown = useMemo(() => {
    const all = topics.data?.topics ?? [];
    const q = needle.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q),
    );
  }, [topics.data, needle]);

  if (topics.isPending) {
    return (
      <div className="page stack-lg">
        <Head />
        <Card flush>
          <SkeletonRows rows={6} />
        </Card>
      </div>
    );
  }
  if (topics.isError) {
    return (
      <div className="page stack-lg">
        <Head />
        <Broken what="your topics" retry={() => void topics.refetch()} />
      </div>
    );
  }

  const weeks = topics.data.weeks;
  const featured = needle ? [] : shown.slice(0, 3);
  const rest = needle ? shown : shown.slice(3);

  return (
    <div className="page stack-lg">
      <div className="between">
        <Head count={topics.data.topics.length} />
        <SearchField
          placeholder="Search topics…"
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          style={{ width: 280 }}
        />
      </div>

      {shown.length === 0 ? (
        <Card flush>
          {needle ? (
            <Empty lead="Nothing by that name.">
              Topics are named from what your meetings were about — try a word somebody would
              actually have said.
            </Empty>
          ) : (
            <Empty tone="ok" lead="Nothing has crossed yet.">
              A subject becomes a topic when it comes up somewhere <strong>else</strong> — a
              stand-up and then a 1:1, a weekly and then a customer call. Something raised only
              inside one recurring meeting is that meeting, and its series page already has it.
            </Empty>
          )}
        </Card>
      ) : null}

      {featured.length > 0 ? (
        <div className="topic-cards">
          {featured.map((t) => (
            <FeaturedTopic key={t.id} topic={t} />
          ))}
        </div>
      ) : null}

      {rest.length > 0 ? (
        <Card flush>
          <div className="row dense topic-head">
            <span className="eyebrow grow">Topic</span>
            <span className="eyebrow topic-col-who">Raised most by</span>
            <span className="eyebrow topic-col-trend">Places · last {weeks} weeks</span>
          </div>
          {rest.map((t) => (
            <TopicRowLine key={t.id} topic={t} />
          ))}
        </Card>
      ) : null}
    </div>
  );
}

function Head({ count }: { count?: number }): ReactElement {
  return (
    <div className="stack" style={{ gap: 4 }}>
      <h1 className="t-title">Topics</h1>
      <p className="mono">
        {count === undefined ? 'what your meetings keep coming back to' : null}
        {count !== undefined
          ? `${count} ${count === 1 ? 'subject' : 'subjects'} that crossed from one meeting to another`
          : null}
      </p>
    </div>
  );
}

/** One of the three most alive: room enough to show its shape. */
function FeaturedTopic({ topic }: { topic: TopicRow }): ReactElement {
  return (
    <Link to={`/t/${topic.id}`} className="topic-card">
      <div className="stack" style={{ gap: 6 }}>
        <span className="topic-card-name">{topic.name}</span>
        {/* ⚠ Places first. A subject that came up in three instances of one
            weekly is that weekly; what makes this a topic is that it crossed
            from one place to another, so that is the number it leads with. */}
        <span className="mono">
          {topic.places} {topic.places === 1 ? 'place' : 'different places'} · {topic.mentions}{' '}
          {topic.mentions === 1 ? 'meeting' : 'meetings'}
          {topic.quiet ? ' · quiet lately' : ''}
        </span>
      </div>
      <Trend bars={topic.trend} tall />
      <div className="topic-card-foot">
        <span className="mono topic-card-span">
          {dateLabel(topic.firstRaisedAt)} → {ago(topic.lastMentionedAt)}
        </span>
        <p className="topic-card-where">{topic.placeNames.slice(0, 3).join(' · ')}</p>
        {topic.description ? <p className="topic-card-what">{topic.description}</p> : null}
      </div>
    </Link>
  );
}

function TopicRowLine({ topic }: { topic: TopicRow }): ReactElement {
  return (
    <Link to={`/t/${topic.id}`} className="row topic-row">
      <div className="row-main grow">
        <span className="row-title">
          {topic.name}
          {topic.quiet ? <Pill>quiet</Pill> : null}
        </span>
        {topic.description ? <span className="row-sub topic-what">{topic.description}</span> : null}
        <span className="row-sub mono topic-where">{topic.placeNames.join(' · ')}</span>
      </div>
      <div className="topic-col-who">
        {topic.mostActive ? (
          <Who email="" name={topic.mostActive.name || guessName('')} />
        ) : (
          <span className="mono">—</span>
        )}
      </div>
      <div className="topic-col-trend cluster" style={{ gap: 10, justifyContent: 'flex-end' }}>
        <span className="mono">{topic.places}</span>
        <Trend bars={topic.trend} />
      </div>
    </Link>
  );
}

/**
 * Mentions per week.
 *
 * ⚠ Drawn to the tallest bar in THIS topic, not across the page: the shape of
 * a subject's life is what the reader is after, and scaling every sparkline to
 * the busiest topic in the workspace would flatten every quiet one into a
 * straight line that says nothing.
 */
export function Trend({ bars, tall = false }: { bars: number[]; tall?: boolean }): ReactElement {
  const top = Math.max(1, ...bars);
  return (
    <span className={`trend ${tall ? 'trend-tall' : ''}`} aria-hidden="true">
      {bars.map((n, i) => (
        <span
          key={i}
          className={`trend-bar ${n > 0 ? 'has' : ''}`}
          style={{ height: `${n > 0 ? Math.max(14, (n / top) * 100) : 6}%` }}
        />
      ))}
    </span>
  );
}
